import { cn } from '@/lib/utils';
import { SkrillLogo } from './SkrillLogo';
import methodCreditCardIcon from '@/assets/method-credit-card.svg';
import methodBankIcon from '@/assets/method-bank.svg';

export type WithdrawMethod = 'card' | 'bank' | 'skrill';

interface Props {
  /** Advance to the chosen method's flow screen. */
  onSelect: (value: WithdrawMethod) => void;
  /** True until a valid amount is entered — every method is locked. */
  disabled: boolean;
}

/**
 * "Withdrawal Methods" grid — tall pill action buttons. Tapping a method
 * advances the flow to that method's confirm screen (ARC-150: the amount is
 * chosen first, then the method IS the call to action; there is no separate
 * submit button on the amount screen). All pills are disabled until a valid
 * amount is entered.
 *
 * Only live payout rails are rendered: unavailable methods (e.g. Interac) are
 * hidden entirely, not shown disabled.
 */
export function WithdrawMethodPills({ onSelect, disabled }: Props) {
  return (
    <div className="grid grid-cols-2 gap-2.5">
      <MethodPill
        onClick={() => onSelect('card')}
        disabled={disabled}
        icon={<img src={methodCreditCardIcon} alt="" aria-hidden="true" className="h-10 w-auto shrink-0" />}
        label="Card"
      />
      <MethodPill
        onClick={() => onSelect('bank')}
        disabled={disabled}
        icon={<img src={methodBankIcon} alt="" aria-hidden="true" className="h-10 w-auto shrink-0" />}
        label="Bank"
      />
      <MethodPill
        onClick={() => onSelect('skrill')}
        disabled={disabled}
        icon={<SkrillLogo width={50} height={22} />}
        label="Skrill"
      />
    </div>
  );
}

interface PillProps {
  onClick: () => void;
  disabled: boolean;
  icon: React.ReactNode;
  label: string;
}

function MethodPill({ onClick, disabled, icon, label }: PillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex h-[52px] items-center gap-2.5 rounded-[40px] border-2 border-stroke-soft bg-surface px-4 text-left transition-all duration-150',
        'hover:border-purple/40 hover:shadow-card disabled:pointer-events-none disabled:opacity-50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/40 focus-visible:ring-offset-2',
      )}
    >
      {icon}
      <span className="text-[18px] font-medium text-ink">{label}</span>
    </button>
  );
}
